import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { PlanningBlock, DayKey } from "@/types/planning";
import { DAYS, timeToMinutes, minutesToTime } from "@/types/planning";
import { getEffectiveDay, computeBlockOffset, type ScheduleMapEntry } from "@/lib/resolveEffectiveSchedule";

/** Block types that make a day entirely unavailable for sport/training placement */
const NO_SPORT_BLOCK_TYPES: Set<string> = new Set(["rest", "blocked"]);

export interface TrainingSession {
  title: string;
  objective?: string;
  duration_minutes?: number;
  level_required?: string;
  exercises?: { name: string; sets?: string; reps?: string }[];
  fact_ids?: string[];
  tags?: string[];
}

export interface NutritionBlock {
  title: string;
  content: string;
  conditions?: string;
}

export interface OverlayBlock {
  id: string;
  day: DayKey;
  start: string;
  end: string;
  category: "training" | "nutrition" | "sleep" | "recovery";
  title: string;
  subtitle?: string;
  session?: TrainingSession;
  nutritionBlock?: NutritionBlock;
  confirmed: boolean;
  scheduledTime?: string | null;
  /** ID of the linked SessionRecord in the sessions table */
  linkedSessionId?: string | null;
}

interface V2DayPlan {
  day: string;
  focus: string;
  session_type: string;
  duration_hint_min?: number;
  intensity_hint?: string;
  recovery?: string;
  nutrition_timing?: string;
  notes?: string;
  session_id?: string;
  create_session_option?: boolean;
}

interface V2WeekPlan {
  week: number;
  days: V2DayPlan[];
}

interface ProgramContent {
  training?: { blocks: TrainingSession[] };
  nutrition?: { blocks: NutritionBlock[] };
  weekly_plan?: string;
  rules?: string[];
  simple?: {
    weekly_plan?: V2WeekPlan[];
    nutrition_rules?: string[];
    recovery_rules?: string[];
    overview?: Record<string, unknown>;
  };
}

// Map French day names → DayKey
const FRENCH_DAY_MAP: Record<string, DayKey> = {
  lundi: "mon", mardi: "tue", mercredi: "wed", jeudi: "thu",
  vendredi: "fri", samedi: "sat", dimanche: "sun",
};

function frenchDayToKey(dayName: string): DayKey | null {
  return FRENCH_DAY_MAP[dayName.toLowerCase().trim()] ?? null;
}

/**
 * Determines which program week index to use based on cycle position.
 *
 * RULE:
 * - cycleWeekIndex is 0-based (current position in cycle)
 * - totalProgramWeeks is the number of weeks defined in the program
 * - We modulo the cycleWeekIndex by totalProgramWeeks to loop correctly.
 * - If cycleWeekIndex is null/undefined → defaults to 0 (first week).
 */
function resolveWeekIndex(cycleWeekIndex: number | undefined | null, totalProgramWeeks: number): number {
  if (!totalProgramWeeks || totalProgramWeeks <= 0) return 0;
  const idx = cycleWeekIndex ?? 0;
  return ((idx % totalProgramWeeks) + totalProgramWeeks) % totalProgramWeeks;
}

/**
 * Maps an active program's sessions onto the user's planning week
 * as a non-intrusive visual overlay (never written directly).
 *
 * cycleWeekIndex: 0-based index of the current week in the cycle.
 *   - null/undefined → defaults to week 0 (legacy / simple planning).
 */
export function useWeeklyPlanOverlay(
  programId: string | null,
  planningBlocks: PlanningBlock[],
  sleepTarget?: { bed: string; wake: string },
  cycleWeekIndex?: number | null,
  scheduleMappings?: ScheduleMapEntry[]
) {
  const { user } = useAuth();
  const [programContent, setProgramContent] = useState<ProgramContent | null>(null);
  const [programTitle, setProgramTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionTimeMap, setSessionTimeMap] = useState<Map<string, { scheduledTime: string | null; dayOfWeek: number | null }>>(new Map());

  // Fetch program content + program_sessions scheduled times
  useEffect(() => {
    if (!programId || !user) {
      setProgramContent(null);
      setSessionTimeMap(new Map());
      return;
    }
    setLoading(true);
    Promise.all([
      supabase
        .from("programs")
        .select("title, content_json")
        .eq("id", programId)
        .single(),
      supabase
        .from("program_sessions")
        .select("session_id, scheduled_time, day_of_week, sort_order")
        .eq("program_id", programId)
        .order("sort_order", { ascending: true }),
    ]).then(([progRes, sessRes]) => {
      if (progRes.data) {
        setProgramContent(progRes.data.content_json as unknown as ProgramContent);
        setProgramTitle(progRes.data.title);
      }
      if (sessRes.data) {
        const map = new Map<string, { scheduledTime: string | null; dayOfWeek: number | null }>();
        for (const ps of sessRes.data) {
          map.set(ps.session_id, { scheduledTime: ps.scheduled_time, dayOfWeek: ps.day_of_week });
        }
        setSessionTimeMap(map);
      }
      setLoading(false);
    }).catch((err) => {
      console.error("[useWeeklyPlanOverlay] load error", err);
      setLoading(false);
    });
  }, [programId, user]);

  // Map sessions to sport blocks on the planning
  const overlayBlocks = useMemo((): OverlayBlock[] => {
    if (!programContent) return [];

    const result: OverlayBlock[] = [];

    // ── 1) Extract training sessions ──
    const v2WeeklyPlan = programContent.simple?.weekly_plan;
    const legacyTrainingBlocks = programContent.training?.blocks || [];

    // Find sport blocks in planning (confirmed slots)
    const sportBlocks = planningBlocks
      .filter((b) => b.type === "sport")
      .sort((a, b) => {
        const dayOrder = DAYS.map((d) => d.key);
        const dayDiff = dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
        if (dayDiff !== 0) return dayDiff;
        return timeToMinutes(a.start) - timeToMinutes(b.start);
      });

    if (v2WeeklyPlan && v2WeeklyPlan.length > 0) {
      // ✅ Resolve the correct week based on cycleWeekIndex
      const weekIdx = resolveWeekIndex(cycleWeekIndex, v2WeeklyPlan.length);
      const targetWeek = v2WeeklyPlan[weekIdx];

      const activeDays = (targetWeek.days || []).filter(
        (d) => d.session_type !== "off" && d.session_type !== "repos" && d.duration_hint_min !== 0
      );

      // Use centralized block offset for consistent indexing across weeks
      const blockOffset = computeBlockOffset(v2WeeklyPlan, weekIdx);

      // Apply schedule map: remap each day to its effective day if a mapping exists
      const mappedActiveDays = activeDays.map((dp, i) => {
        if (!scheduleMappings || scheduleMappings.length === 0) return dp;
        const effectiveDay = getEffectiveDay(blockOffset + i, dp.day, scheduleMappings);
        if (effectiveDay === dp.day) return dp;
        return { ...dp, day: effectiveDay };
      });

      // Build a map: dayKey → V2DayPlan[]
      const dayPlanMap = new Map<DayKey, V2DayPlan[]>();
      for (const dp of mappedActiveDays) {
        const dk = frenchDayToKey(dp.day);
        if (!dk) continue;
        if (!dayPlanMap.has(dk)) dayPlanMap.set(dk, []);
        dayPlanMap.get(dk)!.push(dp);
      }

      if (sportBlocks.length > 0) {
        // Match V2 day plans to existing sport blocks
        const usedPerDay = new Map<DayKey, number>();
        for (const block of sportBlocks) {
          const plans = dayPlanMap.get(block.day);
          if (!plans || plans.length === 0) continue;
          const idx = usedPerDay.get(block.day) ?? 0;
          const dp = plans[idx % plans.length];
          usedPerDay.set(block.day, idx + 1);

          const psInfo = dp.session_id ? sessionTimeMap.get(dp.session_id) : null;
          const scheduledTime = psInfo?.scheduledTime ?? null;

          result.push({
            id: `overlay-training-${block.id}`,
            day: block.day,
            start: block.start,
            end: block.end,
            category: "training",
            title: dp.focus || dp.day,
            subtitle: [
              scheduledTime ? `🕐 ${scheduledTime.slice(0, 5)}` : null,
              dp.duration_hint_min ? `${dp.duration_hint_min} min` : null,
              dp.intensity_hint || null,
            ].filter(Boolean).join(" · ") || undefined,
            session: {
              title: dp.focus || dp.day,
              objective: dp.notes || undefined,
              duration_minutes: dp.duration_hint_min,
              tags: dp.intensity_hint ? [dp.intensity_hint] : undefined,
            },
            confirmed: true,
            scheduledTime,
            linkedSessionId: dp.session_id || null,
          });
        }

        // Unmatched V2 day plans (program says train but no sport block)
        for (const [dk, plans] of dayPlanMap) {
          const used = usedPerDay.get(dk) ?? 0;
          if (used > 0) continue;
          // Skip days marked as rest or blocked — do not place training there
          if (planningBlocks.some((b) => b.day === dk && NO_SPORT_BLOCK_TYPES.has(b.type))) continue;
          for (const dp of plans) {
            result.push({
              id: `overlay-v2-unmatched-${dk}-${dp.day}`,
              day: dk,
              start: "09:00",
              end: minutesToTime(9 * 60 + (dp.duration_hint_min || 60)),
              category: "training",
              title: `⚠️ ${dp.focus || dp.day}`,
              subtitle: `Pas de créneau sport ${DAYS.find(d => d.key === dk)?.label || dk}`,
              session: {
                title: dp.focus || dp.day,
                objective: dp.notes || undefined,
                duration_minutes: dp.duration_hint_min,
              },
              confirmed: false,
              linkedSessionId: dp.session_id || null,
            });
          }
        }
      } else {
        // ✅ FALLBACK: No sport blocks at all — still show program sessions
        // so the user sees what the program expects even without planning blocks
        for (const [dk, plans] of dayPlanMap) {
          // Skip days marked as rest or blocked
          if (planningBlocks.some((b) => b.day === dk && NO_SPORT_BLOCK_TYPES.has(b.type))) continue;
          for (const dp of plans) {
            const psInfo = dp.session_id ? sessionTimeMap.get(dp.session_id) : null;
            const scheduledTime = psInfo?.scheduledTime ?? null;

            result.push({
              id: `overlay-v2-fallback-${dk}-${dp.day}`,
              day: dk,
              start: scheduledTime ? scheduledTime.slice(0, 5) : "09:00",
              end: minutesToTime(
                timeToMinutes(scheduledTime ? scheduledTime.slice(0, 5) : "09:00") +
                (dp.duration_hint_min || 60)
              ),
              category: "training",
              title: `📋 ${dp.focus || dp.day}`,
              subtitle: [
                scheduledTime ? `🕐 ${scheduledTime.slice(0, 5)}` : null,
                dp.duration_hint_min ? `${dp.duration_hint_min} min` : null,
                dp.intensity_hint || null,
                "Aucun créneau planifié",
              ].filter(Boolean).join(" · ") || undefined,
              session: {
                title: dp.focus || dp.day,
                objective: dp.notes || undefined,
                duration_minutes: dp.duration_hint_min,
                tags: dp.intensity_hint ? [dp.intensity_hint] : undefined,
              },
              confirmed: false,
              scheduledTime,
              linkedSessionId: dp.session_id || null,
            });
          }
        }
      }
    } else if (legacyTrainingBlocks.length > 0) {
      // Legacy format: round-robin assignment
      if (sportBlocks.length > 0) {
        sportBlocks.forEach((block, i) => {
          const session = legacyTrainingBlocks[i % legacyTrainingBlocks.length];
          if (session) {
            result.push({
              id: `overlay-training-${block.id}`,
              day: block.day,
              start: block.start,
              end: block.end,
              category: "training",
              title: session.title,
              subtitle: session.objective || `${session.duration_minutes || "?"} min`,
              session,
              confirmed: true,
            });
          }
        });
      } else {
        // ✅ FALLBACK: show legacy training blocks even without sport blocks
        // Skip days marked as rest or blocked
        const availableDays = DAYS.filter(
          (d) => !planningBlocks.some((b) => b.day === d.key && NO_SPORT_BLOCK_TYPES.has(b.type))
        );
        legacyTrainingBlocks.forEach((session, i) => {
          if (availableDays.length === 0) return;
          const targetDay = availableDays[i % availableDays.length].key;
          result.push({
            id: `overlay-legacy-fallback-${i}`,
            day: targetDay,
            start: "09:00",
            end: minutesToTime(9 * 60 + (session.duration_minutes || 60)),
            category: "training",
            title: `📋 ${session.title}`,
            subtitle: session.objective || `${session.duration_minutes || "?"} min · Aucun créneau planifié`,
            session,
            confirmed: false,
          });
        });
      }
    }

    // ── 2) Nutrition overlays ──
    const legacyNutritionBlocks = programContent.nutrition?.blocks || [];
    const v2NutritionRules = programContent.simple?.nutrition_rules || [];

    const mealBlocks = planningBlocks.filter((b) => b.type === "meal");

    if (legacyNutritionBlocks.length > 0) {
      const usedMealIds = new Set<string>();
      for (const nutriBlock of legacyNutritionBlocks) {
        const availableMeal = mealBlocks.find((m) => !usedMealIds.has(m.id));
        if (!availableMeal) break;
        usedMealIds.add(availableMeal.id);
        result.push({
          id: `overlay-nutrition-${availableMeal.id}`,
          day: availableMeal.day,
          start: availableMeal.start,
          end: availableMeal.end,
          category: "nutrition",
          title: nutriBlock.title,
          subtitle: nutriBlock.conditions || undefined,
          nutritionBlock: nutriBlock,
          confirmed: false,
        });
      }
    } else if (v2NutritionRules.length > 0 && mealBlocks.length > 0) {
      const uniqueRules = [...new Set(v2NutritionRules)];
      const usedMealIds = new Set<string>();
      for (const rule of uniqueRules) {
        const availableMeal = mealBlocks.find((m) => !usedMealIds.has(m.id));
        if (!availableMeal) break;
        usedMealIds.add(availableMeal.id);
        result.push({
          id: `overlay-nutrition-v2-${availableMeal.id}`,
          day: availableMeal.day,
          start: availableMeal.start,
          end: availableMeal.end,
          category: "nutrition",
          title: "Conseil nutrition",
          subtitle: rule.length > 60 ? rule.slice(0, 57) + "…" : rule,
          nutritionBlock: { title: "Conseil nutrition", content: rule },
          confirmed: false,
        });
      }
    }

    // ── 3) Sleep recommendations ──
    if (sleepTarget) {
      for (const d of DAYS) {
        result.push({
          id: `overlay-sleep-${d.key}`,
          day: d.key,
          start: sleepTarget.bed,
          end: sleepTarget.wake,
          category: "sleep",
          title: "Sommeil recommandé",
          subtitle: `${sleepTarget.bed} → ${sleepTarget.wake}`,
          confirmed: false,
        });
      }
    }

    return result;
  }, [programContent, planningBlocks, sleepTarget, sessionTimeMap, cycleWeekIndex, scheduleMappings]);

  // Stats
  const sessionCount = overlayBlocks.filter((b) => b.category === "training").length;
  const nutritionCount = overlayBlocks.filter((b) => b.category === "nutrition").length;
  const hasSleepOverlay = overlayBlocks.some((b) => b.category === "sleep");

  return {
    overlayBlocks,
    programContent,
    programTitle,
    sessionCount,
    nutritionCount,
    hasSleepOverlay,
    loading,
  };
}
